import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { generateCode128Svg } from '../utils/code128Svg';
import './OpsReceiving.css';

const API_BASE = process.env.NODE_ENV === 'development'
    ? `${window.location.protocol}//${window.location.hostname}:5001/api`
    : '/api';

// Date Formatter: YYYY-month -DD (e.g. "2026-july -28")
const getFormattedDate = (dateObj = new Date()) => {
    const year = dateObj.getFullYear();
    const months = [
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
    ];
    const month = months[dateObj.getMonth()];
    const day = dateObj.getDate();
    return `${year}-${month} -${day}`;
};

// Web Audio API beep helper for physical scanner feedback
const playBeep = (type) => {
    try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        const ctx = new AudioContext();

        if (ctx.state === 'suspended') {
            ctx.resume();
        }

        if (type === 'success') {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.setValueAtTime(800, ctx.currentTime);
            gain.gain.setValueAtTime(0.08, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.start();
            osc.stop(ctx.currentTime + 0.12);
        } else if (type === 'duplicate') {
            const osc1 = ctx.createOscillator();
            const gain1 = ctx.createGain();
            osc1.type = 'sawtooth';
            osc1.frequency.setValueAtTime(220, ctx.currentTime);
            gain1.gain.setValueAtTime(0.12, ctx.currentTime);
            gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
            osc1.connect(gain1);
            gain1.connect(ctx.destination);
            osc1.start();
            osc1.stop(ctx.currentTime + 0.25);

            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.type = 'sawtooth';
            osc2.frequency.setValueAtTime(170, ctx.currentTime + 0.12);
            gain2.gain.setValueAtTime(0.12, ctx.currentTime + 0.12);
            gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.38);
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.start(ctx.currentTime + 0.12);
            osc2.stop(ctx.currentTime + 0.38);
        }
    } catch (err) {
        console.warn('[Audio feedback] Play beep failure:', err);
    }
};

const LOCATION_OPTIONS = ['PKSLK', 'PKLHE', 'PKKHR'];

const OpsReceiving = ({ onBack, currentUser, isSuperUser, companySlug }) => {
    const [dates, setDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(getFormattedDate());
    const [scans, setScans] = useState([]);
    const [isScanning, setIsScanning] = useState(false);
    const [trackingInput, setTrackingInput] = useState('');
    const [datesLoading, setDatesLoading] = useState(false);
    const [scansLoading, setScansLoading] = useState(false);

    // Location state & Modal
    const [selectedLocation, setSelectedLocation] = useState('PKSLK');
    const [tempLocation, setTempLocation] = useState('PKSLK');
    const [showLocationModal, setShowLocationModal] = useState(false);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    // Auto Details Modal state (pops up when tracking number barcode is scanned)
    const [showScanDetailsModal, setShowScanDetailsModal] = useState(false);
    const [pendingTrackingNumber, setPendingTrackingNumber] = useState('');
    const [pendingWeight, setPendingWeight] = useState('');
    const [pendingCustomerName, setPendingCustomerName] = useState('');
    const [pendingLocation, setPendingLocation] = useState('PKSLK');
    const [savingScan, setSavingScan] = useState(false);

    // Edit Modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editScanId, setEditScanId] = useState(null);
    const [editTrackingNumber, setEditTrackingNumber] = useState('');
    const [editWeight, setEditWeight] = useState('');
    const [editCustomerName, setEditCustomerName] = useState('');
    const [editLocation, setEditLocation] = useState('PKSLK');
    const [updatingScan, setUpdatingScan] = useState(false);

    // Customer Name suggestions list for autocomplete
    const [customerSuggestions, setCustomerSuggestions] = useState([]);

    // Duplicate detection state for pending scan
    const [pendingDuplicateInfo, setPendingDuplicateInfo] = useState(null);

    // Thermal Sticker Print Modal state & Selection state
    const [showPrintModal, setShowPrintModal] = useState(false);
    const [stickerScan, setStickerScan] = useState(null);
    const [selectedScanIds, setSelectedScanIds] = useState([]);

    // Export Modal state
    const [showExportModal, setShowExportModal] = useState(false);
    const [exportFromDate, setExportFromDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split('T')[0];
    });
    const [exportToDate, setExportToDate] = useState(() => new Date().toISOString().split('T')[0]);
    const [exportLocation, setExportLocation] = useState('ALL');
    const [exporting, setExporting] = useState(false);

    const inputRef = useRef(null);
    const weightInputRef = useRef(null);

    // Fetch distinct dates for right sidebar
    const fetchDates = useCallback(async () => {
        setDatesLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/ops-receiving/dates`);
            if (res.data.success) {
                setDates(res.data.dates);
            }
        } catch (err) {
            console.error('[OPS Receiving] Fetching dates failed:', err);
        } finally {
            setDatesLoading(false);
        }
    }, []);

    // Fetch receiving scans for selected date
    const fetchScansForDate = useCallback(async (date) => {
        setScansLoading(true);
        try {
            const res = await axios.get(`${API_BASE}/ops-receiving/scans?date=${encodeURIComponent(date)}`);
            if (res.data.success) {
                setScans(res.data.scans);
            }
        } catch (err) {
            console.error('[OPS Receiving] Fetching scans failed:', err);
        } finally {
            setScansLoading(false);
        }
    }, []);

    // Fetch distinct customer names for suggestions
    const fetchCustomerSuggestions = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE}/ops-receiving/customers`);
            if (res.data.success && Array.isArray(res.data.customers)) {
                setCustomerSuggestions(res.data.customers);
            }
        } catch (err) {
            console.error('[OPS Receiving] Fetching customer suggestions failed:', err);
        }
    }, []);

    useEffect(() => {
        fetchDates();
        fetchCustomerSuggestions();
    }, [fetchDates, fetchCustomerSuggestions]);

    useEffect(() => {
        if (selectedDate) {
            setSelectedScanIds([]);
            fetchScansForDate(selectedDate);
        }
    }, [selectedDate, fetchScansForDate]);

    // Keep input field focused while scanning mode is active
    useEffect(() => {
        if (isScanning && inputRef.current && !showScanDetailsModal && !showLocationModal && !showEditModal && !showPrintModal && !showExportModal) {
            inputRef.current.focus();
        }
    }, [isScanning, showScanDetailsModal, showLocationModal, showEditModal, showPrintModal, showExportModal]);

    // Auto focus weight input when scan details modal opens
    useEffect(() => {
        if (showScanDetailsModal && weightInputRef.current) {
            setTimeout(() => {
                if (weightInputRef.current) weightInputRef.current.focus();
            }, 100);
        }
    }, [showScanDetailsModal]);

    // Start scan button clicked -> Location selection modal
    const handleStartScanningClick = () => {
        setTempLocation(selectedLocation || 'PKSLK');
        setShowLocationModal(true);
    };

    const confirmStartScanning = () => {
        setSelectedLocation(tempLocation);
        setShowLocationModal(false);
        const today = getFormattedDate();
        setSelectedDate(today);
        setIsScanning(true);
    };

    const stopScanMode = () => {
        setIsScanning(false);
        fetchDates();
    };

    // Check duplicate status across all historical dates
    const checkTrackingDuplicate = async (code) => {
        if (!code || !code.trim()) {
            setPendingDuplicateInfo(null);
            return;
        }
        try {
            const res = await axios.get(`${API_BASE}/ops-receiving/check-duplicate?trackingNumber=${encodeURIComponent(code.trim())}`);
            if (res.data.success && res.data.isDuplicate) {
                setPendingDuplicateInfo({
                    isDuplicate: true,
                    priorDates: res.data.priorDates,
                    priorScans: res.data.priorScans
                });
                playBeep('duplicate');
            } else {
                setPendingDuplicateInfo(null);
            }
        } catch (err) {
            console.warn('[OPS Receiving] Duplicate check error:', err);
        }
    };

    // When user scans tracking number (hits Enter or barcode scanner submits)
    const handleBarcodeSubmit = (e) => {
        e.preventDefault();
        const code = trackingInput.trim();
        if (!code) return;

        setTrackingInput(''); // Clear barcode input instantly
        setPendingTrackingNumber(code);
        setPendingWeight('');
        setPendingCustomerName('');
        setPendingLocation(selectedLocation || 'PKSLK');
        setPendingDuplicateInfo(null);
        setShowScanDetailsModal(true); // Automatically open details popup!

        // Instantly check if this barcode exists across all old dates
        checkTrackingDuplicate(code);
    };

    // Save scan details from automatic popup window
    const handleSaveScanDetails = async (e) => {
        if (e) e.preventDefault();
        if (!pendingTrackingNumber.trim()) return;

        setSavingScan(true);
        try {
            const today = getFormattedDate();
            const res = await axios.post(`${API_BASE}/ops-receiving/scan`, {
                trackingNumber: pendingTrackingNumber.trim(),
                weight: pendingWeight ? parseFloat(pendingWeight) : 0,
                customerName: pendingCustomerName.trim(),
                date: selectedDate || today,
                location: pendingLocation || selectedLocation || 'PKSLK'
            });

            if (res.data.success) {
                if (res.data.isDuplicate) {
                    playBeep('duplicate');
                } else {
                    playBeep('success');
                }

                // Add to list and close details popup
                setScans(prev => [res.data.newScan, ...prev]);
                setShowScanDetailsModal(false);
                setPendingDuplicateInfo(null);
                fetchDates();
                fetchCustomerSuggestions();
            }
        } catch (err) {
            console.error('[OPS Receiving] Save scan failed:', err);
            alert(err.response?.data?.message || 'Error saving receiving scan.');
        } finally {
            setSavingScan(false);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    // Edit Detail Modal
    const handleOpenEditModal = (scan) => {
        setEditScanId(scan._id);
        setEditTrackingNumber(scan.trackingNumber || '');
        setEditWeight(scan.weight !== undefined ? scan.weight : '');
        setEditCustomerName(scan.customerName || '');
        setEditLocation(scan.location || 'PKSLK');
        setShowEditModal(true);
    };

    const handleSaveEdit = async (e) => {
        e.preventDefault();
        if (!editScanId) return;

        setUpdatingScan(true);
        try {
            const res = await axios.put(`${API_BASE}/ops-receiving/scan/${editScanId}`, {
                trackingNumber: editTrackingNumber.trim(),
                weight: editWeight ? parseFloat(editWeight) : 0,
                customerName: editCustomerName.trim(),
                location: editLocation
            });

            if (res.data.success) {
                setScans(prev => prev.map(s => s._id === editScanId ? res.data.updatedScan : s));
                setShowEditModal(false);
                fetchCustomerSuggestions();
            }
        } catch (err) {
            console.error('[OPS Receiving] Update scan failed:', err);
            alert(err.response?.data?.message || 'Error updating scan details.');
        } finally {
            setUpdatingScan(false);
        }
    };

    // Delete Scan Record
    const handleDeleteScan = async (id) => {
        if (!window.confirm('Are you sure you want to delete this receiving scan record?')) return;

        try {
            const res = await axios.delete(`${API_BASE}/ops-receiving/scan/${id}`);
            if (res.data.success) {
                setScans(prev => prev.filter(s => s._id !== id));
                fetchDates();
            }
        } catch (err) {
            console.error('[OPS Receiving] Delete scan failed:', err);
            alert(err.response?.data?.message || 'Error deleting scan record.');
        }
    };

    // Print 4x6 Sticker (Single & Batch Print)
    const handleOpenPrintModal = (scan) => {
        setStickerScan(scan);
        setShowPrintModal(true);
    };

    const triggerBatchThermalPrint = (scansToPrint) => {
        if (!scansToPrint || scansToPrint.length === 0) {
            alert('Please select at least one package scan to print stickers.');
            return;
        }

        const printWindow = window.open('', '_blank', 'width=850,height=600');
        if (!printWindow) {
            alert('Please allow popups to print stickers.');
            return;
        }

        const stickersHtml = scansToPrint.map((s, idx) => {
            const barcodeSvg = generateCode128Svg(s.trackingNumber || 'N/A', 75, 2);
            const scanDate = s.timestamp
                ? new Date(s.timestamp).toLocaleString()
                : s.date || '';

            return `
                <div class="label-container ${idx === scansToPrint.length - 1 ? 'last-label' : ''}">
                    <div class="header-box">
                        <div>
                            <div class="title">OPS RECEIVING LABEL</div>
                            <div class="subtitle">INTERNAL PACKAGE RECEIPT</div>
                        </div>
                        <div style="text-align: right; font-weight: 800; font-size: 12px;">
                            LOC: ${s.location || 'PKSLK'}<br/>
                            DATE: ${s.date || ''}
                        </div>
                    </div>

                    <div class="barcode-box">
                        ${barcodeSvg}
                    </div>

                    <div class="tracking-text">
                        ${s.trackingNumber || 'N/A'}
                    </div>



                    <div class="footer-box">
                        <span>TIME: ${scanDate}</span>
                        <span>OPS RECEIVING SYSTEM</span>
                    </div>
                </div>
            `;
        }).join('');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Print Batch 4x6 Stickers (${scansToPrint.length})</title>
                <style>
                    @page {
                        size: 6in 4in landscape;
                        margin: 0;
                    }
                    body {
                        margin: 0;
                        padding: 0;
                        font-family: Arial, Helvetica, sans-serif;
                        background: #ffffff;
                        color: #000000;
                    }
                    .label-container {
                        width: 6in;
                        height: 4in;
                        padding: 0.2in;
                        box-sizing: border-box;
                        display: flex;
                        flex-direction: column;
                        justify-content: space-between;
                        page-break-after: always;
                        break-after: page;
                    }
                    .label-container.last-label {
                        page-break-after: auto;
                        break-after: auto;
                    }
                    .header-box {
                        border-bottom: 2px solid #000000;
                        padding-bottom: 4px;
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                    }
                    .title {
                        font-size: 18px;
                        font-weight: 900;
                        letter-spacing: 1px;
                        margin: 0;
                    }
                    .subtitle {
                        font-size: 11px;
                        font-weight: 700;
                        margin-top: 2px;
                    }
                    .barcode-box {
                        text-align: center;
                        margin: 6px 0 2px 0;
                        width: 100%;
                    }
                    .tracking-text {
                        font-size: 22px;
                        font-weight: 900;
                        font-family: 'Courier New', Courier, monospace;
                        letter-spacing: 2px;
                        text-align: center;
                        border-top: 2px solid #000000;
                        border-bottom: 2px solid #000000;
                        padding: 4px 0;
                        margin: 4px 0 8px 0;
                    }
                    .details-grid {
                        display: grid;
                        grid-template-columns: 1fr 1fr;
                        gap: 6px 20px;
                        font-size: 13px;
                    }
                    .detail-row {
                        display: flex;
                        justify-content: space-between;
                    }
                    .detail-label {
                        font-weight: 700;
                    }
                    .detail-value {
                        font-weight: 900;
                    }
                    .footer-box {
                        border-top: 1px solid #000000;
                        padding-top: 4px;
                        font-size: 10px;
                        display: flex;
                        justify-content: space-between;
                        font-weight: 600;
                    }
                </style>
            </head>
            <body>
                ${stickersHtml}
                <script>
                    window.onload = function() {
                        window.print();
                        setTimeout(function() { window.close(); }, 500);
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const triggerThermalPrint = () => {
        if (!stickerScan) return;
        triggerBatchThermalPrint([stickerScan]);
    };

    // Toggle Select All / Single Select
    const handleToggleSelectAll = () => {
        if (selectedScanIds.length === filteredScans.length && filteredScans.length > 0) {
            setSelectedScanIds([]);
        } else {
            setSelectedScanIds(filteredScans.map(s => s._id).filter(Boolean));
        }
    };

    const handleToggleSelectScan = (id) => {
        if (!id) return;
        setSelectedScanIds(prev =>
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };


    // Filter scans based on search term
    const filteredScans = scans.filter(s => {
        if (!searchTerm.trim()) return true;
        const term = searchTerm.toLowerCase().trim();
        return (
            (s.trackingNumber || '').toLowerCase().includes(term) ||
            (s.customerName || '').toLowerCase().includes(term) ||
            (s.location || '').toLowerCase().includes(term) ||
            (s.scannedBy || '').toLowerCase().includes(term)
        );
    });

    // Calculated metrics
    const totalScansCount = scans.length;
    const totalWeightSum = scans.reduce((acc, curr) => acc + (parseFloat(curr.weight) || 0), 0);
    const uniqueCustomersCount = new Set(scans.map(s => s.customerName).filter(Boolean)).size;

    // Excel Export handler
    const handleDownloadExcel = async () => {
        if (!exportFromDate || !exportToDate) {
            alert('Please select both From Date and To Date');
            return;
        }

        setExporting(true);
        try {
            const res = await axios.get(
                `${API_BASE}/ops-receiving/export?startDate=${exportFromDate}&endDate=${exportToDate}&location=${exportLocation}`
            );

            if (res.data.success && res.data.scans) {
                const exportScans = res.data.scans;
                if (exportScans.length === 0) {
                    alert('No receiving records found for selected date range and location.');
                    setExporting(false);
                    return;
                }

                const excelData = exportScans.map((s, idx) => {
                    const isDup = s.isDuplicate || (s.priorDates && s.priorDates.length > 0) || exportScans.filter(o => o.trackingNumber?.trim().toUpperCase() === s.trackingNumber?.trim().toUpperCase()).length > 1;
                    return {
                        'S.No': idx + 1,
                        'Date': s.date || '',
                        'Time': s.timestamp ? new Date(s.timestamp).toLocaleTimeString() : '',
                        'Location': s.location || 'N/A',
                        'Tracking Number': s.trackingNumber || '',
                        'Status': isDup ? 'Duplicate' : 'Original',
                        'Other Scan Dates': (s.priorDates || []).join(', '),
                        'Customer Name': s.customerName || '',
                        'Weight (KG)': s.weight !== undefined ? s.weight : 0,
                        'Received By': s.scannedBy || ''
                    };
                });

                const worksheet = XLSX.utils.json_to_sheet(excelData);
                worksheet['!cols'] = [
                    { wch: 8 },  // S.No
                    { wch: 18 }, // Date
                    { wch: 14 }, // Time
                    { wch: 12 }, // Location
                    { wch: 28 }, // Tracking Number
                    { wch: 14 }, // Status
                    { wch: 25 }, // Other Scan Dates
                    { wch: 25 }, // Customer Name
                    { wch: 14 }, // Weight
                    { wch: 25 }  // Received By
                ];

                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'OPS Receiving');
                XLSX.writeFile(workbook, `OPS_Receiving_Report_${exportFromDate}_to_${exportToDate}.xlsx`);

                setShowExportModal(false);
            }
        } catch (err) {
            console.error('[OPS Receiving] Download excel failed:', err);
            alert(err.response?.data?.message || 'Error exporting receiving data.');
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="ops-receiving-container">
            {/* Left/Center Main Area */}
            <div className="ops-receiving-main">
                {/* Header Banner */}
                <div className="ops-header">
                    <div className="ops-header-info">
                        <h2>📦 OPS Receiving Module</h2>
                        <p>Package receiving, barcode scanning & thermal sticker printing</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <button className="ops-btn excel" onClick={() => setShowExportModal(true)}>
                            📥 Download Excel
                        </button>
                        {!isScanning ? (
                            <button className="ops-btn primary" onClick={handleStartScanningClick}>
                                🚀 Start Scan
                            </button>
                        ) : (
                            <button className="ops-btn danger" onClick={stopScanMode}>
                                🛑 Stop Scan
                            </button>
                        )}
                        <button className="ops-btn secondary" onClick={onBack} disabled={isScanning}>
                            ↩ Back
                        </button>
                    </div>
                </div>

                {/* Active Scanner Console */}
                {isScanning && (
                    <div className="ops-scanner-console">
                        <div className="ops-status-bar">
                            <div className="ops-status-indicator">
                                <span className="ops-pulse-dot"></span>
                                <span>SCANNER READY & LISTEN MODE ACTIVE</span>
                            </div>
                            <div style={{ fontSize: '0.9rem', color: '#94a3b8' }}>
                                Location: <strong style={{ color: '#38bdf8' }}>{selectedLocation}</strong> | Date: <strong style={{ color: '#38bdf8' }}>{selectedDate}</strong>
                            </div>
                        </div>

                        <form onSubmit={handleBarcodeSubmit} className="ops-input-wrapper">
                            <span className="ops-barcode-icon">🔍</span>
                            <input
                                ref={inputRef}
                                type="text"
                                className="ops-barcode-input"
                                placeholder="Scan tracking barcode here..."
                                value={trackingInput}
                                onChange={(e) => setTrackingInput(e.target.value)}
                                autoFocus
                            />
                        </form>
                    </div>
                )}

                {/* Summary Metrics */}
                <div className="ops-metrics-grid">
                    <div className="ops-metric-card">
                        <div className="ops-metric-icon" style={{ background: '#0284c7', color: 'white' }}>📦</div>
                        <div className="ops-metric-info">
                            <h4>Total Scans Today</h4>
                            <p>{totalScansCount}</p>
                        </div>
                    </div>

                    <div className="ops-metric-card">
                        <div className="ops-metric-icon" style={{ background: '#10b981', color: 'white' }}>⚖️</div>
                        <div className="ops-metric-info">
                            <h4>Total Weight (KG)</h4>
                            <p>{totalWeightSum.toFixed(2)}</p>
                        </div>
                    </div>

                    <div className="ops-metric-card">
                        <div className="ops-metric-icon" style={{ background: '#8b5cf6', color: 'white' }}>👥</div>
                        <div className="ops-metric-info">
                            <h4>Unique Customers</h4>
                            <p>{uniqueCustomersCount}</p>
                        </div>
                    </div>

                    <div className="ops-metric-card">
                        <div className="ops-metric-icon" style={{ background: '#f59e0b', color: 'white' }}>📍</div>
                        <div className="ops-metric-info">
                            <h4>Active Location</h4>
                            <p>{selectedLocation}</p>
                        </div>
                    </div>
                </div>

                {/* Scanned Records Table Card */}
                <div className="ops-table-card">
                    <div className="ops-table-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <h3>Scanned Packages for [{selectedDate}]</h3>
                            <span className="count-badge" style={{ background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', padding: '4px 10px', borderRadius: '4px', fontSize: '0.85rem' }}>
                                Total: {filteredScans.length} | Originals: {filteredScans.filter(s => !(s.isDuplicate || (s.priorDates && s.priorDates.length > 0) || scans.filter(o => o.trackingNumber?.trim().toUpperCase() === s.trackingNumber?.trim().toUpperCase()).length > 1)).length} | Duplicates: {filteredScans.filter(s => s.isDuplicate || (s.priorDates && s.priorDates.length > 0) || scans.filter(o => o.trackingNumber?.trim().toUpperCase() === s.trackingNumber?.trim().toUpperCase()).length > 1).length}
                            </span>
                            {selectedScanIds.length > 0 && (
                                <button
                                    className="ops-btn excel sm"
                                    onClick={() => {
                                        const selectedScans = scans.filter(s => selectedScanIds.includes(s._id));
                                        triggerBatchThermalPrint(selectedScans);
                                    }}
                                >
                                    🖨️ Print Selected Stickers ({selectedScanIds.length})
                                </button>
                            )}
                        </div>
                        <input
                            type="text"
                            className="ops-search-input"
                            placeholder="Search tracking, customer..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="ops-table-wrapper">
                        {scansLoading ? (
                            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Loading scan data...</div>
                        ) : filteredScans.length === 0 ? (
                            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                                {searchTerm ? 'No matching records found.' : 'No packages received on this date yet.'}
                            </div>
                        ) : (
                            <table className="ops-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '36px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={filteredScans.length > 0 && selectedScanIds.length === filteredScans.length}
                                                onChange={handleToggleSelectAll}
                                                title="Select All / Deselect All"
                                                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                            />
                                        </th>
                                        <th>S.NO</th>
                                        <th>TIME</th>
                                        <th>TRACKING NUMBER</th>
                                        <th>STATUS</th>
                                        <th>CUSTOMER NAME</th>
                                        <th>WEIGHT (KG)</th>
                                        <th>LOCATION</th>
                                        <th>RECEIVED BY</th>
                                        <th>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredScans.map((scan, idx) => {
                                        const isChecked = selectedScanIds.includes(scan._id);
                                        return (
                                            <tr key={scan._id || idx} style={{ background: isChecked ? '#1e3a8a' : 'transparent' }}>
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={() => handleToggleSelectScan(scan._id)}
                                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                    />
                                                </td>
                                                <td>{filteredScans.length - idx}</td>
                                                <td>{scan.timestamp ? new Date(scan.timestamp).toLocaleTimeString() : ''}</td>
                                                <td style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#38bdf8' }}>
                                                    {scan.trackingNumber}
                                                </td>
                                                <td>
                                                    {(() => {
                                                        const isDuplicate = scan.isDuplicate || (scan.priorDates && scan.priorDates.length > 0) || scans.filter(s => s.trackingNumber?.trim().toUpperCase() === scan.trackingNumber?.trim().toUpperCase()).length > 1;
                                                        return isDuplicate ? (
                                                            <span 
                                                                className="ops-badge duplicate"
                                                                style={{
                                                                    background: '#7f1d1d',
                                                                    color: '#fca5a5',
                                                                    border: '1px solid #ef4444',
                                                                    padding: '3px 8px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    display: 'inline-block'
                                                                }}
                                                                title={scan.priorDates && scan.priorDates.length > 0 ? `Also scanned on: ${scan.priorDates.join(', ')}` : 'Duplicate scan'}
                                                            >
                                                                ⚠️ Duplicate
                                                                {scan.priorDates && scan.priorDates.length > 0 && (
                                                                    <span style={{ fontSize: '0.7rem', display: 'block', opacity: 0.9, marginTop: '2px' }}>
                                                                        📅 {scan.priorDates.slice(0, 2).join(', ')}{scan.priorDates.length > 2 ? '...' : ''}
                                                                    </span>
                                                                )}
                                                            </span>
                                                        ) : (
                                                            <span 
                                                                className="ops-badge original"
                                                                style={{
                                                                    background: '#14532d',
                                                                    color: '#86efac',
                                                                    border: '1px solid #22c55e',
                                                                    padding: '3px 8px',
                                                                    borderRadius: '4px',
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 700,
                                                                    display: 'inline-block'
                                                                }}
                                                            >
                                                                Original
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td style={{ fontWeight: 600, color: '#f8fafc' }}>
                                                    {scan.customerName || 'N/A'}
                                                </td>
                                                <td style={{ fontWeight: 700, color: '#10b981' }}>
                                                    {scan.weight !== undefined ? scan.weight : 0} KG
                                                </td>
                                                <td>
                                                    <span style={{ background: '#334155', padding: '2px 8px', borderRadius: '4px', fontSize: '0.8rem' }}>
                                                        {scan.location || 'PKSLK'}
                                                    </span>
                                                </td>
                                                <td>{scan.scannedBy || 'N/A'}</td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            className="ops-btn sm secondary"
                                                            onClick={() => handleOpenEditModal(scan)}
                                                            title="Edit Details"
                                                        >
                                                            ✏️ Edit
                                                        </button>
                                                        <button
                                                            className="ops-btn sm excel"
                                                            onClick={() => handleOpenPrintModal(scan)}
                                                            title="Print 4x6 Thermal Sticker"
                                                        >
                                                            🖨️ Print Sticker
                                                        </button>
                                                        <button
                                                            className="ops-btn sm danger"
                                                            onClick={() => handleDeleteScan(scan._id)}
                                                            title="Delete Scan Record"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>

            </div>

            {/* Right Sidebar for Dates Archives */}
            <div className="ops-receiving-sidebar">
                <div className="ops-sidebar-title">
                    <span>📅</span> Receiving Archives
                </div>

                {datesLoading && <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>Loading dates...</div>}

                {!datesLoading && dates.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#64748b', fontSize: '0.85rem', marginTop: '1rem' }}>
                        No receiving archives found.
                    </div>
                )}

                <ul className="ops-date-list">
                    {dates.map(date => (
                        <li
                            key={date}
                            className={`ops-date-item ${selectedDate === date ? 'active' : ''}`}
                            onClick={() => {
                                if (!isScanning) {
                                    setSelectedDate(date);
                                }
                            }}
                            style={{ cursor: isScanning ? 'not-allowed' : 'pointer', opacity: isScanning && selectedDate !== date ? 0.5 : 1 }}
                        >
                            <span>{date}</span>
                            <span>📦</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Location Selection Modal */}
            {showLocationModal && (
                <div className="ops-modal-backdrop">
                    <div className="ops-modal-box">
                        <div className="ops-modal-header">
                            <h3>📍 Select Receiving Location</h3>
                            <button className="ops-btn sm secondary" onClick={() => setShowLocationModal(false)}>✕</button>
                        </div>
                        <div className="ops-form-group">
                            <label>Location Facility</label>
                            <select
                                className="ops-form-control"
                                value={tempLocation}
                                onChange={(e) => setTempLocation(e.target.value)}
                            >
                                {LOCATION_OPTIONS.map(loc => (
                                    <option key={loc} value={loc}>{loc}</option>
                                ))}
                            </select>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.5rem' }}>
                            <button className="ops-btn secondary" onClick={() => setShowLocationModal(false)}>Cancel</button>
                            <button className="ops-btn primary" onClick={confirmStartScanning}>Confirm & Start Scan</button>
                        </div>
                    </div>
                </div>
            )}

            {/* AUTOMATIC POPUP DETAILS MODAL (Triggers upon barcode scan) */}
            {showScanDetailsModal && (
                <div className="ops-modal-backdrop">
                    <div className="ops-modal-box">
                        <div className="ops-modal-header">
                            <h3>📥 Enter Package Details</h3>
                            <button className="ops-btn sm secondary" onClick={() => setShowScanDetailsModal(false)}>✕</button>
                        </div>

                        <form onSubmit={handleSaveScanDetails}>
                            {pendingDuplicateInfo?.isDuplicate && (
                                <div style={{
                                    background: '#450a0a',
                                    border: '1px solid #ef4444',
                                    color: '#fca5a5',
                                    padding: '10px 14px',
                                    borderRadius: '6px',
                                    marginBottom: '1rem',
                                    fontSize: '0.88rem'
                                }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: '4px' }}>
                                        ⚠️ Duplicate Package Detected!
                                    </div>
                                    <div>
                                        This tracking number was already received in: <strong>{pendingDuplicateInfo.priorDates?.join(', ') || 'an older date'}</strong>
                                    </div>
                                    {pendingDuplicateInfo.priorScans && pendingDuplicateInfo.priorScans.length > 0 && (
                                        <div style={{ fontSize: '0.8rem', opacity: 0.9, marginTop: '3px' }}>
                                            First received: {pendingDuplicateInfo.priorScans[0].date} ({new Date(pendingDuplicateInfo.priorScans[0].timestamp).toLocaleTimeString()}) at {pendingDuplicateInfo.priorScans[0].location || 'PKSLK'}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className="ops-form-group">
                                <label>Tracking Number</label>
                                <input
                                    type="text"
                                    className="ops-form-control"
                                    style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#38bdf8' }}
                                    value={pendingTrackingNumber}
                                    onChange={(e) => setPendingTrackingNumber(e.target.value)}
                                    onBlur={() => checkTrackingDuplicate(pendingTrackingNumber)}
                                    required
                                />
                            </div>

                            <div className="ops-form-group">
                                <label>Weight (KG)</label>
                                <input
                                    ref={weightInputRef}
                                    type="number"
                                    step="0.01"
                                    className="ops-form-control"
                                    placeholder="Enter weight e.g. 5.5"
                                    value={pendingWeight}
                                    onChange={(e) => setPendingWeight(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="ops-form-group">
                                <label>Customer Name</label>
                                <input
                                    type="text"
                                    className="ops-form-control"
                                    placeholder="Enter or select customer name (Type to auto-suggest)..."
                                    value={pendingCustomerName}
                                    onChange={(e) => setPendingCustomerName(e.target.value)}
                                    list="ops-customer-name-list"
                                    autoComplete="off"
                                    required
                                />
                                <datalist id="ops-customer-name-list">
                                    {customerSuggestions.map((cust, idx) => (
                                        <option key={idx} value={cust} />
                                    ))}
                                </datalist>
                            </div>

                            <div className="ops-form-group">
                                <label>Location</label>
                                <select
                                    className="ops-form-control"
                                    value={pendingLocation}
                                    onChange={(e) => setPendingLocation(e.target.value)}
                                >
                                    {LOCATION_OPTIONS.map(loc => (
                                        <option key={loc} value={loc}>{loc}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.5rem' }}>
                                <button type="button" className="ops-btn secondary" onClick={() => setShowScanDetailsModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="ops-btn primary" disabled={savingScan}>
                                    {savingScan ? 'Saving...' : '💾 SAVE & CONTINUE SCAN'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* EDIT SCAN DETAILS MODAL */}
            {showEditModal && (
                <div className="ops-modal-backdrop">
                    <div className="ops-modal-box">
                        <div className="ops-modal-header">
                            <h3>✏️ Edit Receiving Record</h3>
                            <button className="ops-btn sm secondary" onClick={() => setShowEditModal(false)}>✕</button>
                        </div>

                        <form onSubmit={handleSaveEdit}>
                            <div className="ops-form-group">
                                <label>Tracking Number</label>
                                <input
                                    type="text"
                                    className="ops-form-control"
                                    value={editTrackingNumber}
                                    onChange={(e) => setEditTrackingNumber(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="ops-form-group">
                                <label>Weight (KG)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="ops-form-control"
                                    value={editWeight}
                                    onChange={(e) => setEditWeight(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="ops-form-group">
                                <label>Customer Name</label>
                                <input
                                    type="text"
                                    className="ops-form-control"
                                    placeholder="Enter or select customer name..."
                                    value={editCustomerName}
                                    onChange={(e) => setEditCustomerName(e.target.value)}
                                    list="ops-customer-name-list"
                                    autoComplete="off"
                                    required
                                />
                            </div>

                            <div className="ops-form-group">
                                <label>Location</label>
                                <select
                                    className="ops-form-control"
                                    value={editLocation}
                                    onChange={(e) => setEditLocation(e.target.value)}
                                >
                                    {LOCATION_OPTIONS.map(loc => (
                                        <option key={loc} value={loc}>{loc}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.5rem' }}>
                                <button type="button" className="ops-btn secondary" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="ops-btn primary" disabled={updatingScan}>
                                    {updatingScan ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* PRINT 4x6 STICKER MODAL (LANDSCAPE) */}
            {showPrintModal && stickerScan && (
                <div className="ops-modal-backdrop">
                    <div className="ops-modal-box" style={{ maxWidth: '640px' }}>
                        <div className="ops-modal-header">
                            <h3>🖨️ 4x6 Thermal Sticker Preview (Landscape)</h3>
                            <button className="ops-btn sm secondary" onClick={() => setShowPrintModal(false)}>✕</button>
                        </div>

                        <div className="thermal-sticker-preview">
                            <div className="sticker-header">
                                <div>
                                    <div className="sticker-title">OPS RECEIVING LABEL</div>
                                    <div className="sticker-subtitle">INTERNAL PACKAGE RECEIPT</div>
                                </div>
                                <div style={{ textAlign: 'right', fontWeight: '800', fontSize: '0.8rem' }}>
                                    LOC: {stickerScan.location || 'PKSLK'}<br />
                                    DATE: {stickerScan.date || ''}
                                </div>
                            </div>

                            <div className="sticker-barcode-box" dangerouslySetInnerHTML={{ __html: generateCode128Svg(stickerScan.trackingNumber || 'N/A', 75, 2) }} />

                            <div className="sticker-tracking-num">
                                {stickerScan.trackingNumber || 'N/A'}
                            </div>



                            <div style={{ borderTop: '1px solid #000', paddingTop: '6px', fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                                <span>TIME: {stickerScan.timestamp ? new Date(stickerScan.timestamp).toLocaleTimeString() : ''}</span>
                                <span>OPS RECEIVING SYSTEM</span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.5rem' }}>
                            <button className="ops-btn secondary" onClick={() => setShowPrintModal(false)}>Close</button>
                            <button className="ops-btn primary" onClick={triggerThermalPrint}>🖨️ Print 4x6 Label</button>
                        </div>
                    </div>
                </div>
            )}

            {/* EXPORT EXCEL MODAL */}
            {showExportModal && (
                <div className="ops-modal-backdrop">
                    <div className="ops-modal-box">
                        <div className="ops-modal-header">
                            <h3>📥 Download Receiving Report</h3>
                            <button className="ops-btn sm secondary" onClick={() => setShowExportModal(false)}>✕</button>
                        </div>

                        <div className="ops-form-group">
                            <label>From Date</label>
                            <input
                                type="date"
                                className="ops-form-control"
                                value={exportFromDate}
                                onChange={(e) => setExportFromDate(e.target.value)}
                            />
                        </div>

                        <div className="ops-form-group">
                            <label>To Date</label>
                            <input
                                type="date"
                                className="ops-form-control"
                                value={exportToDate}
                                onChange={(e) => setExportToDate(e.target.value)}
                            />
                        </div>

                        <div className="ops-form-group">
                            <label>Location</label>
                            <select
                                className="ops-form-control"
                                value={exportLocation}
                                onChange={(e) => setExportLocation(e.target.value)}
                            >
                                <option value="ALL">ALL LOCATIONS</option>
                                {LOCATION_OPTIONS.map(loc => (
                                    <option key={loc} value={loc}>{loc}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '1.5rem' }}>
                            <button className="ops-btn secondary" onClick={() => setShowExportModal(false)}>Cancel</button>
                            <button className="ops-btn excel" onClick={handleDownloadExcel} disabled={exporting}>
                                {exporting ? 'Generating...' : '📥 Export Excel'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OpsReceiving;
